import React from 'react';
import { useQuery, useMutation, useApolloClient } from '@apollo/react-hooks';
import gql from 'graphql-tag';
import classnames from 'classnames';

import { Modal, DefaultModalContent, Loader } from 'components';
import { refreshIntegrationsQuery } from 'services/apollo/queries';

import { Logger } from 'services';
import { useTranslation } from 'hooks/useTranslation';

import { ManageAppsIntegrationIcon } from 'screens/ManageAppScreen';

import Icon from 'assets/icons/';
import { PlainButton } from 'components';

import styles from './ConnectedIntegrations.module.scss';

const ConnectedIntegrationsQuery = gql`
  query getConnectedIntegrations {
    me {
      integrations {
        id
        title
        type
        integrationUserId
        features {
          configurable
        }
      }
    }
  }
`;

const removeIntegrationMutation = gql`
  mutation removeIntegration($id: String!) {
    deleteUserIntegration(id: $id) {
      ok
      error
    }
  }
`;

export const ConnectedIntegrations = () => {
  const { t } = useTranslation();
  const client = useApolloClient();
  const [modalIsOpen, setIsOpen] = React.useState(false);
  const [integrationIdToRemove, setIntegrationIdToRemove] = React.useState('');
  const [removeIntegration] = useMutation(removeIntegrationMutation);
  const openModal = () => {
    setIsOpen(true);
  };

  const closeModal = () => {
    setIsOpen(false);
  };

  const { error, data } = useQuery(ConnectedIntegrationsQuery, {
    fetchPolicy: 'cache-and-network',
  });

  if (data === undefined) return <Loader />;

  if (error) return <Loader color="Red" />;

  const {
    me: { integrations },
  } = data;

  return (
    <div className={styles.section}>
      <h2 className={styles.title}>{t('manage-apps-screen.connected-accounts')}</h2>
      <div className={classnames(styles.integrations, false && styles.short)}></div>
      {integrations.map((integration: any) => (
        <div key={integration.id} className={styles.item}>
          <ManageAppsIntegrationIcon
            className={styles.icon}
            itemIntegrationName={integration.type!.slice(0, integration.type!.indexOf('@'))}
          />
          <div className={styles.nameIDwrap}>
            <span className={styles.name}>{integration.title}</span>
            <span className={styles.email}>({integration.integrationUserId})</span>
          </div>
          <PlainButton
            className={classnames('hasTooltip tooltipAboveRight', styles.remove)}
            onClick={() => {
              setIntegrationIdToRemove(integration.id);
              openModal();
            }}
            tooltipText={t('manage-apps-screen.remove-account')}
          >
            <Icon.Close />
          </PlainButton>
        </div>
      ))}
      <Modal isOpen={modalIsOpen} onRequestClose={closeModal}>
        <DefaultModalContent
          title={t('manage-apps-screen.delete-integration-modal.title')}
          subtext={t('manage-apps-screen.delete-integration-modal.subtext')}
          cancelButtonText={t('manage-apps-screen.delete-integration-modal.cancelButtonText')}
          onCancel={closeModal}
          actionButtonText={t('manage-apps-screen.delete-integration-modal.actionButtonText')}
          onAction={() => {
            removeIntegration({ variables: { id: integrationIdToRemove } }).then((data: any) => {
              const {
                data: {
                  deleteUserIntegration: { error, ok },
                },
              } = data;

              if (error) {
                // TODO Add error logging
                Logger.log(error);
              }

              if (ok) {
                client
                  .query({
                    query: refreshIntegrationsQuery,
                    fetchPolicy: 'network-only',
                  })
                  .then(() => {
                    closeModal();
                  })
                  .catch((error) => {
                    Logger.error(error);
                  });
              }
            });
          }}
        />
      </Modal>
    </div>
  );
};
